import React, { useState, useEffect, useRef } from "react";
import { Modal, Slider, InputNumber, Row, Col, message, Spin, Button } from "antd";
import Axios from "../Axios";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function VideoSplitModal({ visible, onClose, video }) {
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState([0, 0]);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!video?.video_url_cdn) return;

    const vid = document.createElement("video");
    vid.preload = "metadata";

    const onLoaded = () => {
      setDuration(vid.duration);
      setRange([0, vid.duration]);
      vid.removeEventListener("loadedmetadata", onLoaded);
      vid.src = "";
    };

    vid.addEventListener("loadedmetadata", onLoaded);
    vid.src = video.video_url_cdn;

    return () => {
      vid.removeEventListener("loadedmetadata", onLoaded);
      vid.src = "";
    };
  }, [video]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (range[0] >= 0 && range[0] <= duration) {
      videoRef.current.currentTime = range[0];
    }
  }, [range[0], duration]);

  const onChangeRange = (value) => setRange(value);

  const onChangeStart = (value) => {
    setRange([Math.min(value, range[1]), range[1]]);
  };

  const onChangeEnd = (value) => {
    setRange([range[0], Math.max(value, range[0])]);
  };

  const handleSave = async () => {
    if (range[0] >= range[1]) {
      message.error("El tiempo de inicio debe ser menor que el tiempo de fin");
      return;
    }
    setLoading(true);
    try {
      await Axios.post(`/videos/${video.id}/split`, {
        video_id: video.id,
        start_time: formatTime(range[0]),
        end_time: formatTime(range[1]),
      });
      message.success("Fragmento guardado correctamente");
      onClose();
    } catch (err) {
      message.error(err.message || "Error inesperado");
    } finally {
      setLoading(false);
    }
  };

const handleFinish = () => {
  Modal.confirm({
    title: "¿Finalizar divisiones?",
    content: "El video se quitará de la bandeja. ¿Estás seguro de que deseas finalizar?",
    okText: "Sí, finalizar",
    cancelText: "Cancelar",
    okButtonProps: { danger: true },
    onOk: async () => {
      setLoading(true);
      try {
        const response = await Axios.post(`/videos/${video.id}/finish`);
        message.success(response.data.message || "Divisiones finalizadas correctamente");
        onClose();
      } catch (err) {
        const backendMessage =
          err?.response?.data?.message || err.message || "Error al finalizar divisiones";
        message.error(backendMessage);
      } finally {
        setLoading(false);
      }
    },
  });
};

  return (
    <Modal
      title={`Dividir video: ${video?.id}`}
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="finish" onClick={handleFinish} disabled={loading}>
          Finalizar divisiones
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSave}>
          Dividir video
        </Button>,
      ]}
    >
      {!video ? (
        <Spin />
      ) : (
        <>
          <video
            ref={videoRef}
            src={video.video_url_cdn}
            controls
            style={{ width: "100%", height: 400, marginBottom: 20 }}
          />
          <Row>
            <Col span={24}>
              <label>Seleccionar rango</label>
              <Slider
                range
                min={0}
                max={duration}
                step={0.1}
                value={range}
                onChange={onChangeRange}
                disabled={loading}
              />
            </Col>
          </Row>

          <Row gutter={16} align="middle">
            <Col span={8}>
              <label>Inicio (segundos)</label>
              <InputNumber
                min={0}
                max={duration}
                step={0.1}
                value={range[0]}
                onChange={onChangeStart}
                style={{ width: "100%" }}
                disabled={loading}
              />
            </Col>
            <Col span={8}>
              <label>Fin (segundos)</label>
              <InputNumber
                min={0}
                max={duration}
                step={0.1}
                value={range[1]}
                onChange={onChangeEnd}
                style={{ width: "100%" }}
                disabled={loading}
              />
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
}

export default VideoSplitModal;
